import React, { useState, useEffect, useRef, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import { observer, inject } from 'mobx-react';
import { displayError } from '@aws-ee/base-ui/dist/helpers/notification';
import styled from 'styled-components';
import { PaginatedProgress } from '../../components';
import RatingScaleQuestion from './RatingScaleQuestion';
import TextQuestion from './TextQuestion';
import MultipleChoiceQuestion from './MultipleChoiceQuestion';
import MultiSelectQuestion from './MultiSelectQuestion';
import SensitivityScaleQuestion from './SensitivityScaleQuestion';

const PageSize = 5;

const QuestionType = {
  RatingScale: 'RatingScale',
  Text: 'Text',
  MultipleChoice: 'MultipleChoice',
  MultiSelect: 'MultiSelect',
  SensitivityScale: 'SensitivityScale',
};

const Container = styled.div`
  /* Take exactly the space left over in the page's flex column and scroll inside it. Using
     height: 100% here made this the full column height regardless of what sat above it, and a flex
     item's default min-height: auto meant it could not shrink to compensate -- so once the page
     grew an assigned-project header, the questions overflowed and scrolling moved to an ancestor,
     which is not what the paging code resets. */
  flex: 1 1 auto;
  min-height: 0;
  padding: 60px 150px 30px 150px;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }
`;

const SurveyContent = ({ userLearningId, pathId, moduleId, survey, surveyStore }) => {
  const [answers, setAnswers] = useState(new Map());
  const [pageIndex, setPageIndex] = useState(0);
  const [submissionInProgress, setSubmissionInProgress] = useState(false);
  const ref = useRef(null);

  // Compute visible questions based on showIf conditions
  const visibleQuestions = useMemo(() => {
    return survey.questions.filter(q => {
      if (!q.showIf) return true;
      const parentQuestion = survey.questions.find(p => p.key === q.showIf.key);
      if (!parentQuestion) return false;
      const parentAnswer = answers.get(parentQuestion.id);
      if (!parentAnswer) return false;
      // Support both single value and values array
      const matchValues = q.showIf.values && q.showIf.values.length > 0 ? q.showIf.values : [q.showIf.value];
      return matchValues.includes(parentAnswer.value);
    });
  }, [survey.questions, answers]);

  useEffect(() => {}, [answers]);

  // Reset to the top of the new page AFTER React has swapped the questions in. Doing it inside the
  // click handler scrolled the outgoing page and the animation was then disrupted by the re-render,
  // so the reader landed partway down the new page instead of on its first question.
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = 0;
  }, [pageIndex]);

  const nextHandler = async index => {
    setPageIndex(index);
  };

  const submitHandler = async () => {
    try {
      setSubmissionInProgress(true);
      // Only submit answers for visible questions
      const visibleIds = new Set(visibleQuestions.map(q => q.id));
      const submission = {
        userLearningId,
        pathId,
        moduleId,
        questions: Array.from(answers, ([questionId, answer]) => ({ questionId, answer })).filter(q =>
          visibleIds.has(q.questionId),
        ),
      };
      await surveyStore.createSurveySubmission(survey.id, submission);
      setSubmissionInProgress(false);
      window.open(`/learn/${userLearningId}`, '_self').focus();
    } catch (error) {
      setSubmissionInProgress(false);
      displayError(error);
    }
  };

  // A cleared textarea or a deselected multi-select is not an answer. Without this, typing then
  // clearing leaves an entry in the map, the progress counter treats the question as answered, and
  // Submit enables -- only for the server's completeness check to reject the submission.
  const isBlank = value =>
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0);

  const updateAnswer = (questionId, type, value) => {
    const newAnswers = new Map([...answers]);
    if (isBlank(value)) newAnswers.delete(questionId);
    else newAnswers.set(questionId, { type, value });

    // Remove answers for questions that become hidden due to this change
    const answeredQuestion = survey.questions.find(q => q.id === questionId);
    if (answeredQuestion && answeredQuestion.key) {
      survey.questions.forEach(q => {
        if (q.showIf && q.showIf.key === answeredQuestion.key) {
          const matchValues =
            q.showIf.values && q.showIf.values.length > 0 ? q.showIf.values : [q.showIf.value];
          if (!matchValues.includes(value)) {
            newAnswers.delete(q.id);
          }
        }
      });
    }

    setAnswers(newAnswers);
  };

  const pagedQuestions = visibleQuestions.slice(pageIndex * PageSize, pageIndex * PageSize + PageSize);

  // Count only answers for visible questions.
  //
  // PaginatedProgress gates Next on an exact equality against the page boundary, so an unanswered
  // optional question would strand the learner on whichever page it lands. Credit optional
  // questions, but only once every required question is answered -- otherwise the credit would
  // also let a required question be skipped to reach the same boundary. Both instruments put their
  // single optional question last (verify-survey-pairing.js asserts optional-last), so in practice
  // this only ever unblocks the final Submit.
  const visibleIds = new Set(visibleQuestions.map(q => q.id));
  const answeredCount = Array.from(answers.keys()).filter(id => visibleIds.has(id)).length;
  const allRequiredAnswered = visibleQuestions.every(q => q.optional || answers.has(q.id));
  const optionalCredit = allRequiredAnswered
    ? visibleQuestions.filter(q => q.optional && !answers.has(q.id)).length
    : 0;
  const visibleAnswerCount = answeredCount + optionalCredit;

  return (
    <Container className="overflow-auto" ref={ref}>
      {pagedQuestions.map((q, i) => {
        if (q.type === QuestionType.RatingScale)
          return (
            <RatingScaleQuestion
              key={q.id}
              index={pageIndex * PageSize + i}
              {...q}
              disabled={submissionInProgress}
              onClick={v => updateAnswer(q.id, q.type, v)}
            />
          );
        if (q.type === QuestionType.Text)
          return (
            <TextQuestion
              key={q.id}
              index={pageIndex * PageSize + i}
              {...q}
              disabled={submissionInProgress}
              onTextChange={v => updateAnswer(q.id, q.type, v)}
            />
          );
        if (q.type === QuestionType.MultipleChoice)
          return (
            <MultipleChoiceQuestion
              key={q.id}
              index={pageIndex * PageSize + i}
              {...q}
              disabled={submissionInProgress}
              onClick={v => updateAnswer(q.id, q.type, v)}
            />
          );
        if (q.type === QuestionType.MultiSelect)
          return (
            <MultiSelectQuestion
              key={q.id}
              index={pageIndex * PageSize + i}
              {...q}
              disabled={submissionInProgress}
              onClick={v => updateAnswer(q.id, q.type, v)}
            />
          );
        if (q.type === QuestionType.SensitivityScale)
          return (
            <SensitivityScaleQuestion
              key={q.id}
              index={pageIndex * PageSize + i}
              {...q}
              disabled={submissionInProgress}
              onClick={v => updateAnswer(q.id, q.type, v)}
            />
          );

        return <></>;
      })}
      <PaginatedProgress
        pageIndex={pageIndex}
        progress={visibleAnswerCount}
        total={visibleQuestions.length}
        pageSize={PageSize}
        progressIds={answers}
        totalIds={pagedQuestions.map(q => q.id)}
        loading={submissionInProgress}
        enforceDisabled
        onNext={nextHandler}
        onSubmit={submitHandler}
      />
    </Container>
  );
};

export default inject('surveyStore')(withRouter(observer(SurveyContent)));
